<template>
  <container class="special-orders">
    <zaproo-breadcrumbs class="special-orders-breadcrumbs">
      <zaproo-breadcrumb :to="home()">
        {{ $t("Home") }}
      </zaproo-breadcrumb>
      <zaproo-breadcrumb :to="specialOrders()">
        {{ $t("Special orders") }}
      </zaproo-breadcrumb>
    </zaproo-breadcrumbs>

    <special-orders-page-skeleton v-if="pending" />

    <div v-else class="special-orders-content">
      <template v-if="data?.specialOrdersPageData">
        <page-heading class="special-orders-title">
          {{ data.specialOrdersPageData.attributes.title }}
        </page-heading>
        <div class="special-orders-description">
          {{ data.specialOrdersPageData.attributes.descriptions }}
        </div>
      </template>
      <div v-if="list.length" class="special-orders-grid">
        <special-orders-details-card
          v-for="(item, index) in list"
          :key="item.id"
          :instance="item"
          :media-preload="
            index && index < 4 ? { fetchPriority: 'high' } : false
          "
        />
      </div>
    </div>
  </container>
</template>

<script setup lang="ts">
import type { SpecialOrdersDetailsCard as ISpecialOrdersDetailsCard } from "../interfaces";
import SpecialOrdersDetailsCard from "../components/SpecialOrdersDetailsCard.vue";
import SpecialOrdersPageSkeleton from "../components/SpecialOrdersPageSkeleton.vue";

import type {
  StrapiSpecialOrderPage,
  StrapiSpecialOrders,
} from "~/types/strapi";
import type { Strapi4RequestParamsExtended } from "~/types/strapi/base/Strapi4RequestParamsExtended";

const { findOne, find } = useStrapiExtend();
const { home, specialOrders } = useLinker();
const { t } = useI18n();

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    et: "/eritellimused",
    ru: "/specialnye-zakazy",
    fi: "/erikoistilaukset",
    lt: "/specialus-uzsakymai",
    lv: "/specialie-pasutijumi",
  },
});

const list = computed<ISpecialOrdersDetailsCard[]>(
  () =>
    data.value?.specialOrdersList.map((i) => ({
      id: i.id,
      title: i.attributes.title || "",
      slug: i.attributes.slug || "",
      cover: i.attributes.cover.data.attributes || null,
    })) || [],
);

const { data, pending } = await useSyncCachedAsyncData(
  "specialOrdersPageData",
  async () => {
    const { data: specialOrdersPageData } =
      await findOne<StrapiSpecialOrderPage>("special-order-page", {
        "populate[special_orders][fields][0]": "id",
      } as Strapi4RequestParamsExtended);

    const specialOrdersListIds =
      specialOrdersPageData?.attributes?.special_orders?.data.map(
        (obj) => obj.id,
      ) || [];

    const { data } = await find<StrapiSpecialOrders>("special-orders", {
      populate: {
        cover: "*",
      },
      filters: {
        id: specialOrdersListIds,
      },
    });

    data.sort(
      (x, y) =>
        specialOrdersListIds.indexOf(x.id) - specialOrdersListIds.indexOf(y.id),
    );

    return {
      specialOrdersPageData,
      specialOrdersList: data,
    };
  },
);

useSeoPack(
  computed(() => ({
    title: t("Special orders"),
  })),
);
</script>

<style lang="scss">
.special-orders {
  &-breadcrumbs {
    @apply max-sm:pt-5 lg:pb-7 lg:pt-2;
  }

  &-title {
    @apply pb-3 pt-6 max-sm:text-2xl md:pb-2 md:pt-8;
  }

  &-description {
    @apply text-neutral-500 max-sm:pb-3 md:pb-8 md:text-lg;
  }

  &-content {
    @apply pb-10;
  }

  &-grid {
    @apply grid grid-cols-2 gap-x-2 gap-y-4 md:grid-cols-3 md:gap-x-6 lg:grid-cols-5;

    @media (min-width: 1025px) and (max-width: 1270px) {
      @apply grid-cols-4;
    }
  }
}
</style>
